import { useEffect, useState, useCallback, useMemo } from 'react';
import DocumentHeader from '@components/DocumentHeader';
import track, { useTracking } from 'react-tracking';
import { CenteredHeading } from './styled';
import { SWS_HOST } from '@/constants';
import loadable from '@loadable/component';
import { OneColumn } from '@components/Layout';
import GlobalStyle from '@/styled/styled.default';
import { useTheme, LoadingWarren, Column } from '@simplywallst/ui-core';
import useUser from '@/hooks/useUser';
import ScrollLoader from '@components/ScrollLoader';
import If from '@components/If';
import Footer from '@components/Footer';
import { flatten } from 'lodash';
import { useGridCompanies } from '@/hooks/useGridCompanies';
import { useGridRules } from '@/hooks/useGridRules';
import { useMetaData } from '../../hooks/useMetaData';
import { useReduxStateForDiscover } from '@/pages/DiscoverPageV2/hooks/useReduxStateForDiscover';
import { updateRule } from '@ducks/grid/utils';
import {
  ROUTE_INVESTING_IDEA_MARKET_INDUSTRY,
  ROUTE_INVESTING_IDEA_MARKET,
} from '@/constants/routes';
import DocumentSchema from '@components/DocumentSchema';
import type {
  Corporation,
  ItemList,
  ListItem,
  SearchResultsPage,
} from 'schema-dts';

/* loadable */
const BasicHeading = loadable(() => import('../BasicHeading'));
const StockTiles = loadable(
  () => import('@components/Stocks/components/StockTiles')
);
const StockTable = loadable(
  () => import('@components/Stocks/components/StockTable')
);
const Pagination = loadable(
  () => import('@components/Stocks/components/Pagination')
);
const GuestRegistrationModalOnScroll = loadable(
  () =>
    import(
      '@components/GuestRegistrationModal/components/GuestRegistrationModalOnScroll'
    )
);

type StocksView = 'table' | 'tile';

export const PAGE_SIZE = 24;

const DiscoverPageV1 = () => {
  /**
   * Redux
   */
  const {
    id = '0',
    name: slug = 'name',
    market = 'global',
    industry = 'any',
    pageQuery,
    canonicalUrl,
    hasAuthToken,
    navigateTo,
    getPaginatedRoute,
    onAdvancedFilters,
    preFetchCompany,
  } = useReduxStateForDiscover();

  const route =
    industry !== 'any'
      ? ROUTE_INVESTING_IDEA_MARKET_INDUSTRY
      : ROUTE_INVESTING_IDEA_MARKET;
  /** Hooks */
  const theme = useTheme();
  const { trackEvent } = useTracking();

  const {
    data: user,
    mutate: mutateUser,
    isFetched: isUserFetched,
  } = useUser();

  const isGuestUser = isUserFetched ? !user.loggedIn : !hasAuthToken;
  const [stocksView, setStocksView] = useState<StocksView>();

  useEffect(() => {
    trackEvent({
      type: 'track',
      subject: 'page',
      action: 'landed',
      id,
    });
  }, [id, trackEvent]);

  useEffect(() => {
    if (isUserFetched && typeof stocksView === 'undefined' && user.loggedIn) {
      setStocksView(user.defaultStocksView);
    }
  }, [isUserFetched, user, stocksView]);

  const {
    data: { rules, name, description, onlyMarketFiltered, crawlable },
    status: gridRulesStatus,
  } = useGridRules({ id });

  const updatedRules = useMemo(
    () =>
      typeof rules === 'undefined' || rules === ''
        ? undefined
        : updateRule(rules)(market, industry),
    [rules, market, industry]
  );

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetching: isFetchingCompanies,
    isFetched,
  } = useGridCompanies({
    rules: updatedRules,
    pageQuery,
    pageSize: PAGE_SIZE,
    isInfinite: isGuestUser === false,
  });

  const isFetching = gridRulesStatus === 'loading' || isFetchingCompanies;
  const { title, documentTitle } = useMetaData(name, market, industry);
  const totalRecords = data.pages[0].meta.totalRecords;
  const companies = useMemo(
    () => flatten(data.pages.map((page) => page.companies)),
    [data]
  );

  /** Handlers */
  const handleViewSelect = useCallback(
    (value: StocksView) => {
      setStocksView(value);
      if (user.loggedIn) {
        mutateUser({
          settings: {
            stocks_view: value,
          },
        });
      }
    },
    [mutateUser, user]
  );

  return (
    <OneColumn>
      <GlobalStyle background={theme.color.charcoal} />
      <DocumentHeader
        title={documentTitle}
        allowCrawlers={companies.length >= 10 && crawlable}
        description={`${title} - ${description}`}
      >
        {companies.length > 0 && (
          <link
            rel="canonical"
            href={`${SWS_HOST}${canonicalUrl}${
              pageQuery === 1 ? '' : '?page=' + pageQuery
            }`}
          />
        )}
      </DocumentHeader>
      {RUNTIME_ENV === 'server' && (
        <DocumentSchema<SearchResultsPage>
          item={{
            '@context': 'https://schema.org',
            '@type': 'SearchResultsPage',
            identifier: `${SWS_HOST}${canonicalUrl}#webpage`,
            url: `${SWS_HOST}${canonicalUrl}`,
            mainEntity: [
              {
                '@type': 'ItemList',
                name: `${title} - Investing Ideas`,
                itemListOrder: 'http://schema.org/ItemListOrderAscending',
                itemListElement: data.pages[0].companies.map((n, i) => {
                  return {
                    '@type': 'ListItem',
                    position: i + 1,
                    item: {
                      '@type': 'Corporation',
                      name: n.shortName,
                      legalName: n.legalName,
                      tickerSymbol: n.tickerSymbol,
                      url: `${SWS_HOST}${n.slug}`,
                      logo: n.logoUrl,
                      foundingDate: n.yearFounded,
                      description: n.shortDescription,
                    } as Corporation,
                  } as ListItem;
                }),
              } as ItemList,
            ],
            isPartOf: {
              '@type': 'WebSite',
              url: 'https://simplywall.st',
              name: 'Simply Wall Street',
            },
          }}
        />
      )}
      <BasicHeading
        totalRecords={totalRecords}
        stocksView={stocksView || 'table'}
        onViewSelect={handleViewSelect}
        onlyMarketFiltered={onlyMarketFiltered || false}
        rules={rules as string}
        id={id as string}
        name={title}
        description={description as string}
        selectedMarket={market}
        selectedIndustry={industry}
        onMarketUpdate={(market) =>
          navigateTo({
            route,
            market,
            industry,
          })
        }
        onIndustryUpdate={(nextIndustry) =>
          navigateTo({
            route:
              nextIndustry !== 'any'
                ? ROUTE_INVESTING_IDEA_MARKET_INDUSTRY
                : ROUTE_INVESTING_IDEA_MARKET,
            market,
            industry: nextIndustry,
          })
        }
        onAdvancedFilterClick={() => onAdvancedFilters(title)}
        slug={slug as string}
      />
      <ScrollLoader
        handleLoadRequest={() => {
          if (hasNextPage) {
            fetchNextPage();
          }
        }}
        muteScrollEvent={isGuestUser}
      >
        {() =>
          typeof stocksView === 'undefined' || stocksView === 'table' ? (
            <StockTable
              companies={companies}
              industry={industry}
              market={market}
              isFetching={isFetching}
              isFetched={isFetched}
              onLongHover={preFetchCompany}
              hideIndustryCol={onlyMarketFiltered || false}
            />
          ) : (
            <StockTiles
              companies={companies}
              industry={industry}
              market={market}
              onLongHover={preFetchCompany}
              hideIndustryCol={onlyMarketFiltered || false}
              isFetching={isFetching}
              isFetched={isFetched}
            />
          )
        }
      </ScrollLoader>
      <If match={isFetched && totalRecords === 0}>
        <Column>
          <CenteredHeading>No results found.</CenteredHeading>
        </Column>
      </If>
      <If match={isFetching && !isGuestUser}>
        <LoadingWarren width={70} />
      </If>
      <If match={isGuestUser}>
        <Pagination
          currentPage={pageQuery}
          totalPages={data.pages[0].meta.totalPages}
          actionLink={(page) => getPaginatedRoute(route, page)}
          loading={isFetching}
        />
      </If>
      <If match={isGuestUser}>
        <Footer />
      </If>
      <If match={isGuestUser}>
        <GuestRegistrationModalOnScroll />
      </If>
    </OneColumn>
  );
};

export default track(
  {
    type: 'track',
    subject: 'page',
    action: 'landed',
  },
  { dispatchOnMount: true }
)(DiscoverPageV1) as typeof DiscoverPageV1;
